import { PaymentModalityType } from '@/common/types/companies/companies.type'
import { formatCurrency, truncateText } from '@/common/utils/formatters'
import { CompanyContractConsumptionControlledBy } from '@/features/company/types/contract.type'
import { GovernmentAllocationData } from '@/features/governmentAllocations/types/governmentAllocation.type'
import { BoxesIcon, FileBoxIcon } from 'lucide-react'
import { Dispatch, SetStateAction, useId } from 'react'
import { Tooltip } from 'react-tooltip'
import CompanySummaryOfAllAllocatedAndConsumedHoverCard from '../CompanySummaryOfAllAllocatedAndConsumedHoverCard'
import { AllocatedAndConsumedDetails } from '../../types/companySummaryOfAllAllocatedAndConsumed.type'
import useUserPermissions from '@/common/hooks/useUserPermissions'

type GovernmentAllocationDetailsProps = {
  governmentAllocation: GovernmentAllocationData
  paymentModality?: PaymentModalityType
  controlledBy?: CompanyContractConsumptionControlledBy;
  setGovernmentAllocationIdToSearchSummaryInfo: Dispatch<SetStateAction<string>>
  summaryOfAllAllocatedAndConsumed?: AllocatedAndConsumedDetails
  summaryOfAllAllocatedAndConsumedIsPending?: boolean
}

export const GovernmentAllocationDetails = ({
  governmentAllocation,
  paymentModality,
  controlledBy,
  setGovernmentAllocationIdToSearchSummaryInfo,
  summaryOfAllAllocatedAndConsumed,
}: GovernmentAllocationDetailsProps) => {
  const tooltipId = useId()

  const entryProductsCategories = governmentAllocation.entry_product_categories_without_products || [];
  const entryProducts = governmentAllocation.entry_products || [];

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToListSummaryOfAllAllocatedAndConsumed =
    isUserPermissionsPending || hasUserPermission('Client::Billing::GovernmentAllocations::SummaryOfAllAllocatedAndConsumed')

  const showHoverCard = controlledBy === 'quantity' && userHasPermissionToListSummaryOfAllAllocatedAndConsumed

  return (
    <div className="flex gap-4 items-end w-full">
      <div className='w-full'>
        <div 
          className="flex flex-col justify-start gap-2 w-full"
          onMouseEnter={() => {
            setGovernmentAllocationIdToSearchSummaryInfo(governmentAllocation.id)
          }}
        >
          <div className='grow flex items-center justify-between gap-2'>
            <>
              <div className='flex items-center gap-2'>
                <p className="text-xs font-bold leading-3 cursor-default" data-tooltip-id={`${tooltipId}_display_name_tooltip`}>
                  {governmentAllocation.main_entry.office_number} - {truncateText(governmentAllocation.branch_display_name, 30)}
                </p>
                {showHoverCard && (
                  <CompanySummaryOfAllAllocatedAndConsumedHoverCard
                    governmentAllocation={governmentAllocation}
                    summaryOfAllAllocatedAndConsumed={summaryOfAllAllocatedAndConsumed}
                  />
                )}
              </div>

              <Tooltip id={`${tooltipId}_display_name_tooltip`} className="z-50">
                {governmentAllocation.branch_display_name}
              </Tooltip>
            </>

            {[...entryProductsCategories, ...entryProducts].length > 0 && (
              <>
                <div className='flex items-center gap-1' data-tooltip-id={`${tooltipId}_entry_products_tooltip`}>
                  {entryProductsCategories.length > 0 ? (
                    <>
                      <BoxesIcon strokeWidth={1.5} className='size-5' />

                      <p className="text-xs leading-3 cursor-default italic">
                        {governmentAllocation.entry_product_categories_without_products[0].product_category.display_name}
                      </p>
                    </>
                  ) : (
                    <>
                      <FileBoxIcon strokeWidth={1.5} className='size-5' />

                      <p className="text-xs leading-3 cursor-default italic">
                        {governmentAllocation.entry_products[0].product.display_name}
                      </p>
                    </>
                  )}

                  {[...entryProductsCategories, ...entryProducts].length > 1 && (
                    <div className='bg-primary-blue px-2 py-1 rounded-full ml-1'>
                      <p className="text-xs font-bold leading-3 cursor-default">
                        + {[...entryProductsCategories, ...entryProducts].length - 1}
                      </p>
                    </div>
                  )}
                </div>

                <Tooltip id={`${tooltipId}_entry_products_tooltip`} className="z-50 !p-0" clickable>
                  <div className='flex flex-col max-h-80 overflow-y-auto p-3 divide-y divide-white-200 [&>*]:py-3 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0'>
                    {entryProductsCategories.length > 0 && (
                      <div className='flex flex-col gap-y-2'>
                        <div className='flex items-center gap-1'>
                          <BoxesIcon strokeWidth={1.5} className='size-4 text-white-400' />

                          <p className="text-xs leading-3 italic text-white-400">
                            Categorias
                          </p>
                        </div>

                        <div className='flex flex-col divide-y divide-white-100'>
                          {entryProductsCategories.map(({ product_category }) => (
                            <p key={product_category.display_name} className='py-1 first:pt-0 last:pb-0'>
                              {product_category.display_name}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {entryProducts.length > 0 && (
                      <div className='flex flex-col gap-y-2'>
                        <div className='flex items-center gap-1'>
                          <FileBoxIcon strokeWidth={1.5} className='size-4 text-white-400' />

                          <p className="text-xs leading-3 italic text-white-400">
                            Produtos
                          </p>
                        </div>

                        <div className='flex flex-col divide-y divide-white-100'>
                          {entryProducts.map(({ product }) => (
                            <p key={product.display_name} className='py-1 first:pt-0 last:pb-0'>
                              {product.display_name}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Tooltip>
              </>
            )}
          </div>

          {controlledBy !== CompanyContractConsumptionControlledBy.Quantity ? (
            <div className="flex flex-row gap-2">
              <div className="flex flex-col gap-2">
                <div className='flex flex-col gap-2'>
                  <p className="text-[10px] font-light italic leading-3">
                    {paymentModality === 'pre_paid' ? 'Empenhado' : 'Cotado'}
                  </p>
                  <p className="text-xs font-bold leading-3">{formatCurrency(Number(governmentAllocation.total_value))}</p>
                </div>

                <div className="text-yellow-400 flex flex-col gap-2">
                  <p className="text-[10px] font-light italic leading-3">Consumido</p>
                  <p className="text-xs font-bold leading-3">{formatCurrency(Number(governmentAllocation.consumed_total))}</p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className='flex flex-col gap-2'>
                  <p className="text-[10px] font-light italic leading-3">Aprovado</p>
                  <p className="text-xs font-bold leading-3">{formatCurrency(Number(governmentAllocation.approved_value))}</p>
                </div>

                <div className="text-valid flex flex-col gap-2">
                  <p className="text-[10px] font-light italic leading-3">Disponível</p>
                  <p className="text-xs font-bold leading-3">{formatCurrency(Number(governmentAllocation.remaining_total))}</p>
                </div>
              </div>
            </div>
          ) : <div className='h-[72px]' />}
        </div>
      </div>
    </div>
  )
}
